/**
 * BotSpot Web Dashboard Authentication API
 *
 * API functions for user authentication and registration.
 */

import { apiClient } from "./client";
import type {
  LoginRequest,
  TokenResponse,
  RegisterRequest,
  User,
  MessageResponse,
} from "./types";

/**
 * Login with username and password
 *
 * @param credentials - Login credentials
 * @returns Token response with access token
 */
export async function login(credentials: LoginRequest): Promise<TokenResponse> {
  // OAuth2 password flow expects form data
  const formData = new URLSearchParams();
  formData.append("username", credentials.username);
  formData.append("password", credentials.password);

  const response = await apiClient.post<TokenResponse>("/auth/login", formData, {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });

  // Store the token in the client
  apiClient.setToken(response.access_token);

  return response;
}

/**
 * Register a new user account
 *
 * @param userData - User registration data
 * @returns Created user object
 */
export async function register(userData: RegisterRequest): Promise<User> {
  return await apiClient.post<User>("/auth/register", userData);
}

/**
 * Logout the current user
 *
 * Clears the authentication token from local storage.
 */
export function logout(): void {
  apiClient.clearToken();
}

/**
 * Get the current authenticated user's profile
 *
 * @returns User profile data
 */
export async function getCurrentUser(): Promise<User> {
  return await apiClient.get<User>("/auth/me");
}

/**
 * Attempt login via oauth2-proxy headers
 *
 * Called on app init — if oauth2-proxy is active, the proxy auth headers
 * are forwarded automatically by nginx and the API gateway exchanges
 * them for a JWT.
 *
 * @returns Token response if proxy auth succeeds, null otherwise
 */
export async function proxyLogin(): Promise<TokenResponse | null> {
  try {
    const response = await apiClient.post<TokenResponse>("/auth/proxy-login");
    apiClient.setToken(response.access_token);
    return response;
  } catch {
    return null;
  }
}

/**
 * Check if user is currently authenticated
 *
 * @returns True if authenticated
 */
export function isAuthenticated(): boolean {
  return apiClient.isAuthenticated();
}

/**
 * Refresh the access token
 *
 * Note: Implementation depends on backend refresh token strategy.
 * This is a placeholder for future implementation.
 *
 * @returns New token response
 */
export async function refreshToken(): Promise<TokenResponse> {
  // TODO: Implement refresh token logic when backend supports it
  throw new Error("Refresh token not yet implemented");
}

/**
 * Change user password
 *
 * @param currentPassword - Current password
 * @param newPassword - New password
 * @returns Success message
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<MessageResponse> {
  return await apiClient.post<MessageResponse>("/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

/**
 * Request password reset
 *
 * @param email - User's email address
 * @returns Success message
 */
export async function requestPasswordReset(email: string): Promise<MessageResponse> {
  return await apiClient.post<MessageResponse>("/auth/password-reset", {
    email,
  });
}

/**
 * Verify email address
 *
 * @param token - Email verification token
 * @returns Success message
 */
export async function verifyEmail(token: string): Promise<MessageResponse> {
  return await apiClient.post<MessageResponse>("/auth/verify-email", {
    token,
  });
}
